import { api } from "./base";
/**
 * «Релиз трека»: заявка на выпуск готовой песни на стриминговых площадках.
 *
 * Флоу: на входе смотрим заявки на трек (`GET /releases?audio_id=…`) — если она
 * уже отправлена, показываем статус; иначе создаём/переиспользуем черновик
 * (`POST /releases` идемпотентен по активному DRAFT), правки идут `PATCH`'ем,
 * отправка — `POST /releases/{id}/submit` (списывает 1 RELEASES).
 * После SUBMITTED заявка не редактируется (бэк отдаёт 409).
 *
 * Заявку можно адресовать двумя способами: ПО ТРЕКУ (`audio_id` — сценарий
 * «оформить релиз», может создать черновик) и ПО ЗАЯВКЕ (`GET /releases/{id}` —
 * чистое чтение, для списка «Мои релизы»).
 *
 * Базовый путь: /api/v1/public/releases.
 *
 * Заголовки авторизации (Bearer / initData / X-Session-Id) навешивает
 * axios-интерсептор `api` — здесь их не дублируем (как в shares.ts / voice.ts).
 */
const RELEASES_BASE = "/api/v1/public/releases";
/** Лимиты полей (совпадают с валидацией бэка). */
export const RELEASE_TITLE_MAX = 200;
export const RELEASE_AUTHOR_ALIAS_MAX = 128;
export const RELEASE_CONTACT_MAX = 256;
export const RELEASE_TEXT_MAX = 10000;
/** Обложка-файл: whitelist типов и лимит размера (как на бэке). */
export const RELEASE_COVER_MIME = ["image/jpeg", "image/png", "image/webp"];
export const RELEASE_COVER_MAX_BYTES = 10 * 1024 * 1024;
/** Статус заявки в нижнем регистре (бэк может отдать имя члена enum'а). */
export const releaseStatusOf = (order) => String(order.status).toLowerCase();
/** Черновик — единственное состояние, в котором заявку можно править. */
export const isReleaseDraft = (order) => releaseStatusOf(order) === "draft";
/**
 * Заявка уже ушла в работу (submitted / in_progress / published).
 *
 * Для таких НЕЛЬЗЯ создавать новый черновик: `POST /releases` идемпотентен
 * только по активному DRAFT (частичный уникальный индекс `WHERE status='DRAFT'`),
 * поэтому рядом с отправленной заявкой легально появился бы второй черновик —
 * и повторная отправка списала бы ещё один RELEASES за тот же трек. Отклонённая
 * (`rejected`) заявка сюда не входит: по ней релиз оформляют заново.
 */
export const isReleaseInWork = (order) => {
    const status = releaseStatusOf(order);
    return (status === "submitted" || status === "in_progress" || status === "published");
};
/**
 * Создать (или переиспользовать) черновик заявки на аудио. Дефолты подставляет
 * бэк: название/текст из `Audio`, контакт по правилу
 * `username → billing_email → email`. Псевдоним автора дефолта НЕ имеет —
 * поле открывается пустым, его заполняет юзер под каждый релиз.
 */
export const createRelease = async (payload) => {
    const response = await api.post(RELEASES_BASE, payload);
    return response.data;
};
/** Список своих заявок (свежие сверху), опционально по треку/статусу. */
export const listReleases = async (params) => {
    const response = await api.get(RELEASES_BASE, { params });
    return response.data;
};
/**
 * Одна заявка по её id — адресация «по заявке», а не «по треку».
 *
 * Нужна списку «Мои релизы»: на один трек заявок может быть несколько (например
 * отклонённая и новая), и вход по `audio_id` их не различает — а ещё он
 * СОЗДАЁТ черновик, когда активной заявки нет. Чтение заявки ничего не создаёт.
 * Чужая/несуществующая — 404 (бэк не даёт перебирать id).
 *
 * Единственная ручка с лентой событий (`events`): переписку с администратором
 * и историю статусов можно получить только здесь.
 */
export const getRelease = async (releaseId) => {
    const response = await api.get(`${RELEASES_BASE}/${releaseId}`);
    return response.data;
};
/** Правка черновика (не-DRAFT → 409). */
export const updateRelease = async (releaseId, patch) => {
    const response = await api.patch(`${RELEASES_BASE}/${releaseId}`, patch);
    return response.data;
};
/**
 * Загрузить свою обложку (multipart, поле `file`). Content-Type снимаем
 * (undefined), чтобы браузер сам выставил boundary — тот же приём, что в
 * `uploadSharePhoto`.
 */
export const uploadReleaseCover = async (releaseId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    const response = await api.post(`${RELEASES_BASE}/${releaseId}/cover`, formData, { headers: { "Content-Type": undefined } });
    return response.data;
};
/**
 * Отправить заявку в работу. Списывает 1 RELEASES; при нехватке баланса бэк
 * отдаёт стандартную ошибку недостатка средств (её ловит `isInsufficientBalanceError`).
 */
export const submitRelease = async (releaseId) => {
    const response = await api.post(`${RELEASES_BASE}/${releaseId}/submit`, {});
    return response.data;
};
